import React, { useCallback } from 'react';

import { Box, ButtonBase, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { GENERATE_CLARIFY_RECOMMENDED_BADGE } from './GenerateExpression.constants';
import {
  clarifyOptionCardContentStyles,
  clarifyOptionCardHeaderStyles,
  clarifyOptionCardRecommendedStyles,
  clarifyOptionCardStyles,
  clarifyOptionDescriptionStyles,
  clarifyOptionRecommendedBadgeStyles,
  clarifyOptionValueStyles,
} from './GenerateExpression.styles';
import { GenerateExpressionTestIds } from './GenerateExpression.testIds';

const buildCardStyles = isRecommended =>
  isRecommended
    ? { ...clarifyOptionCardStyles, ...clarifyOptionCardRecommendedStyles }
    : clarifyOptionCardStyles;

const ClarifyOptionCard = ({ option, isRecommended = false, isDisabled = false, onSelect }) => {
  const { value, label, description } = option;
  const hasLabel = !_.isNil(label) && !_.isEmpty(label);
  const hasDescription = !_.isNil(description) && !_.isEmpty(description);
  const primaryText = hasLabel ? label : value;
  const shouldShowValue = hasLabel;

  const onCardClick = useCallback(() => onSelect(value), [onSelect, value]);

  return (
    <ButtonBase
      data-testid={GenerateExpressionTestIds.MODAL_CLARIFY_OPTION_CARD}
      disabled={isDisabled}
      sx={buildCardStyles(isRecommended)}
      onClick={onCardClick}
    >
      <Box sx={clarifyOptionCardContentStyles}>
        <Box sx={clarifyOptionCardHeaderStyles}>
          <Typography
            component="span"
            data-testid={GenerateExpressionTestIds.MODAL_CLARIFY_OPTION_LABEL}
            variant="bold14"
          >
            {primaryText}
          </Typography>
          {isRecommended && (
            <Typography
              color="primary.contrastText"
              component="span"
              data-testid={GenerateExpressionTestIds.MODAL_CLARIFY_OPTION_RECOMMENDED}
              sx={clarifyOptionRecommendedBadgeStyles}
              variant="bold12"
            >
              {GENERATE_CLARIFY_RECOMMENDED_BADGE}
            </Typography>
          )}
        </Box>
        {hasDescription && (
          <Typography
            color="text.secondary"
            component="span"
            data-testid={GenerateExpressionTestIds.MODAL_CLARIFY_OPTION_DESCRIPTION}
            sx={clarifyOptionDescriptionStyles}
            variant="regular14"
          >
            {description}
          </Typography>
        )}
        {shouldShowValue && (
          <Box
            component="span"
            data-testid={GenerateExpressionTestIds.MODAL_CLARIFY_OPTION_VALUE}
            sx={clarifyOptionValueStyles}
          >
            {value}
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
};

ClarifyOptionCard.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  isRecommended: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

export default ClarifyOptionCard;
